import { useEffect, useRef, useState } from "react";
import { useLang } from "../hooks/useLang";
import Icon from "./icons";
import Markdown from "./Markdown";
import { listDocuments, type GeneratedDocMeta } from "../services/documents";
import { analyzeDocuments, editDocuments, analysisDocxBlob } from "../services/docAnalysis";
import { getMe, type Me } from "../services/api";

const PAGE_SIZE = 5;

// Doküman Analiz — üstte oluşturulan dokümanlar (sayfalı), altında iki dosya
// yükleme alanı (GSPR + referanslar) + prompt + Analyze; sonuç ekranda + DOCX indir.
export default function DocAnalysis() {
  const { t, lang } = useLang();
  const [docs, setDocs] = useState<GeneratedDocMeta[]>([]);
  const [page, setPage] = useState(0);

  // Ana doküman: ya oluşturulan dokümanlardan SEÇ (selectedDocId) ya da dosya YÜKLE
  // (mainFile) — biri seçilince diğeri temizlenir.
  const [selectedDocId, setSelectedDocId] = useState<number | null>(null);
  const [mainFile, setMainFile] = useState<File | null>(null);
  const [refFiles, setRefFiles] = useState<File[]>([]);
  const [prompt, setPrompt] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [me, setMe] = useState<Me | null>(null);
  // Analize göre düzenleme (500 kredi) — analiz sonucu üzerinden GSPR'yi düzeltir.
  const [editing, setEditing] = useState(false);
  const [editResult, setEditResult] = useState<string | null>(null);
  const [editErr, setEditErr] = useState<string | null>(null);

  useEffect(() => {
    listDocuments().then((r) => setDocs((r.documents || []).filter((d) => d.status !== "preparing"))).catch(() => {});
    getMe().then(setMe).catch(() => {});
  }, []);

  // İşlem ücretleri + özellik aktif mi (admin bayrağı). Ücretler vurgu olarak gösterilir.
  const price = me?.price_doc_analysis_credits ?? 250;
  const editPrice = me?.price_doc_edit_credits ?? 500;
  const active = !!me?.doc_analysis_active;

  const fmtDate = (s: string) => (s ? new Date(s).toLocaleDateString(lang === "en" ? "en-GB" : "tr-TR") : "");
  const pages = Math.max(1, Math.ceil(docs.length / PAGE_SIZE));
  const pageDocs = docs.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const canAnalyze = !!mainFile || selectedDocId !== null || refFiles.length > 0;
  // Ana doküman tek yoldan seçilir: ya listeden SEÇ ya da dosya YÜKLE. Biri
  // aktifken diğeri kilitlenir (kaldırınca tekrar açılır).
  const docSelected = selectedDocId !== null;
  const fileUploaded = !!mainFile;

  async function onAnalyze() {
    if (!canAnalyze) { setErr(t("da_need_file")); return; }
    setErr(null); setResult(null); setEditResult(null); setEditErr(null); setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("prompt", prompt);
      if (mainFile) fd.append("main", mainFile);
      else if (selectedDocId) fd.append("main_doc_id", String(selectedDocId));
      refFiles.forEach((f) => fd.append("refs", f));
      const r = await analyzeDocuments(fd);
      setResult(r.result);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setAnalyzing(false);
    }
  }

  // Analize göre düzelt — analiz sonucu (bulgular) + ana GSPR ile düzeltilmiş doküman üretir.
  async function onEdit() {
    if (!result) return;
    setEditErr(null); setEditResult(null); setEditing(true);
    try {
      const fd = new FormData();
      fd.append("analysis", result);
      fd.append("prompt", prompt);
      if (mainFile) fd.append("main", mainFile);
      else if (selectedDocId) fd.append("main_doc_id", String(selectedDocId));
      const r = await editDocuments(fd);
      setEditResult(r.result);
    } catch (e) {
      setEditErr(e instanceof Error ? e.message : String(e));
    } finally {
      setEditing(false);
    }
  }

  async function onDownload(content: string, titleBase: string) {
    if (!content) return;
    setDownloading(true);
    try {
      const title = (mainFile?.name?.replace(/\.[^.]+$/, "") || titleBase);
      const blob = await analysisDocxBlob(title, content);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = titleBase.replace(/\s+/g, "-").toLowerCase() + "-" + Date.now() + ".docx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="settings" style={{ maxWidth: "100%" }}>
      <h2>{t("docanalysis_title")}</h2>
      <p className="hint">{t("da_intro")}</p>

      {/* Ana doküman: SOLDA oluşturulanlardan seç, SAĞDA dosya yükle — yan yana (biri) */}
      <div className="da-block">
        <h3>{t("da_main_doc")}</h3>
        <p className="hint">{t("da_main_doc_hint")}</p>
        <div className={"da-main-cols" + (docs.length === 0 ? " single" : "")}>
          {docs.length > 0 && (
            <div className={"da-main-col" + (fileUploaded ? " da-disabled" : "")}>
              <ul className="da-doclist">
                {pageDocs.map((d) => {
                  const sel = selectedDocId === d.id;
                  return (
                    <li
                      key={d.id}
                      className={"da-doc-select" + (sel ? " selected" : "")}
                      aria-disabled={fileUploaded}
                      onClick={() => { if (fileUploaded) return; setSelectedDocId(sel ? null : d.id); if (!sel) setMainFile(null); }}
                    >
                      <span className={"da-radio" + (sel ? " on" : "")} aria-hidden>{sel ? "✓" : ""}</span>
                      <span className="da-doc-info">
                        <span className="da-docname">{d.product_name || `#${d.id}`}</span>
                        <span className="hint da-docmeta">{fmtDate(d.created_at)}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
              {pages > 1 && (
                <div className="da-pager">
                  <button className="btn-sec" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>←</button>
                  <span className="hint">{page + 1} / {pages}</span>
                  <button className="btn-sec" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>→</button>
                </div>
              )}
              {fileUploaded && <p className="hint da-lock-note">🔒 {t("da_locked_by_upload")}</p>}
            </div>
          )}
          {docs.length > 0 && <div className="da-or-v"><span>{t("da_or")}</span></div>}
          <div className="da-main-col">
            <FileDrop
              label={t("da_upload_file")}
              hint={t("da_upload_gspr_hint")}
              multiple={false}
              files={mainFile ? [mainFile] : []}
              onFiles={(fs) => { setMainFile(fs[0] || null); if (fs[0]) setSelectedDocId(null); }}
              disabled={docSelected}
              t={t}
            />
            {docSelected && <p className="hint da-lock-note">🔒 {t("da_locked_by_select")}</p>}
          </div>
        </div>
      </div>

      {/* Referans dokümanlar — dışarıdan yüklenir */}
      <div className="da-block">
        <FileDrop
          label={t("da_upload_refs")}
          hint={t("da_upload_refs_hint")}
          multiple
          files={refFiles}
          onFiles={setRefFiles}
          t={t}
        />
      </div>

      {/* Prompt */}
      <label className="da-prompt">
        <span>{t("da_prompt_label")}</span>
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={t("da_prompt_ph")} rows={4} />
      </label>

      {err && <div className="error">{t("err_prefix")}: {err}</div>}

      {/* İşlem ücreti vurgusu — her zaman görünür */}
      <div className="da-cost-badge">🪙 {t("da_cost_badge").replace("{n}", String(price))}</div>
      {/* Aktif değilse (varsayılan) pasif uyarısı + kapalı buton */}
      {!active && <div className="da-soon-notice">⏳ {t("da_analyze_soon")}</div>}
      <div className="da-run">
        <button
          className="btn-pri"
          disabled={!active || analyzing || !canAnalyze}
          onClick={onAnalyze}
          title={active ? "" : t("da_analyze_soon")}
        >
          🔎 {t("da_analyze")}{active ? ` — ${price} ${t("credits_short")}` : ""}
        </button>
      </div>

      {/* Analiz/düzenleme sürerken belirgin modal */}
      {(analyzing || editing) && (
        <div className="gen-modal-overlay" role="alertdialog" aria-busy="true">
          <div className="gen-modal">
            <div className="gen-modal-spinner" aria-hidden />
            <h3>{editing ? t("da_editing") : t("da_analyzing")}</h3>
            <p>{t("da_analyzing_body")}</p>
            <div className="gen-progress"><div className="gen-progress-bar" /></div>
          </div>
        </div>
      )}

      {result && (
        <div className="da-result">
          <div className="da-result-head">
            <h3>{t("da_result")}</h3>
            <div className="da-result-actions">
              {/* Analize göre düzelt — bulgulara göre GSPR'yi düzeltir (500 kredi) */}
              <button className="btn-pri" disabled={!active || editing} onClick={onEdit} title={active ? "" : t("da_analyze_soon")}>
                ✏️ {t("da_edit_btn")}{active ? ` — ${editPrice} ${t("credits_short")}` : ""}
              </button>
              <button className="btn-sec" disabled={downloading} onClick={() => onDownload(result, "analiz")}>
                {downloading ? (<><span className="spinner-sm" /> {t("da_download_docx")}</>) : (<>⬇ {t("da_download_docx")}</>)}
              </button>
            </div>
          </div>
          <div className="da-result-body md-body"><Markdown text={result} /></div>
        </div>
      )}

      {editErr && <div className="error">{t("err_prefix")}: {editErr}</div>}

      {editResult && (
        <div className="da-result">
          <div className="da-result-head">
            <h3>✏️ {t("da_edit_result")}</h3>
            <button className="btn-sec" disabled={downloading} onClick={() => onDownload(editResult, "duzeltme")}>
              {downloading ? (<><span className="spinner-sm" /> {t("da_download_docx")}</>) : (<>⬇ {t("da_download_docx")}</>)}
            </button>
          </div>
          <div className="da-result-body md-body"><Markdown text={editResult} /></div>
        </div>
      )}
    </div>
  );
}

// FileDrop — dosya seçme + sürükle-bırak alanı; seçili dosyaları listeler.
// disabled: başka yol (liste seçimi) aktifken alan kilitlenir.
function FileDrop({ label, hint, multiple, files, onFiles, disabled, t }: {
  label: string; hint: string; multiple: boolean; files: File[];
  onFiles: (fs: File[]) => void; disabled?: boolean; t: (k: string) => string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const accept = ".pdf,.docx,.txt,.md,.csv";

  const add = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const arr = Array.from(list);
    onFiles(multiple ? [...files, ...arr] : [arr[0]]);
  };

  return (
    <div className={"da-drop-wrap" + (disabled ? " da-disabled" : "")}>
      <div className="da-drop-label">{label}</div>
      <div
        className={"da-drop" + (over ? " over" : "")}
        onClick={() => { if (!disabled) inputRef.current?.click(); }}
        onDragOver={(e) => { if (disabled) return; e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { if (disabled) return; e.preventDefault(); setOver(false); add(e.dataTransfer.files); }}
      >
        <Icon name="file" size={22} />
        <div className="da-drop-hint">{hint}</div>
        <div className="da-drop-formats">PDF · DOCX · TXT</div>
        <input ref={inputRef} type="file" accept={accept} multiple={multiple} hidden disabled={disabled}
          onChange={(e) => { add(e.target.files); e.currentTarget.value = ""; }} />
      </div>
      {files.length > 0 && (
        <ul className="da-filelist">
          {files.map((f, i) => (
            <li key={i}>
              <span className="da-fname">{f.name}</span>
              <button className="da-fremove" aria-label={t("da_remove")} onClick={() => onFiles(files.filter((_, j) => j !== i))}>×</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
